import React from "react";
import {
  UsersIcon,
  UserCheckIcon,
  LogOutIcon,
  CoffeeIcon,
  UserXIcon,
} from "../../Icons";
import { BRAND_THEME } from "../../ui/brandTheme";

/**
 * Punch attendance summary rings — Patient indigo brand (shared by Payroll).
 * Values stay semantic; chrome uses one brand tone for reuse/scalability.
 */
const CircularProgress = ({ value, total, label, icon: Icon }) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className={`flex flex-col items-center p-4 bg-white rounded-xl border shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-200 ${BRAND_THEME.section.wrap}`}
    >
      <div className="relative w-24 h-24 mb-3">
        <svg className="w-full h-full -rotate-90" aria-hidden>
          <circle
            cx="48"
            cy="48"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            className="text-indigo-100"
          />
          <circle
            cx="48"
            cy="48"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            strokeDasharray={circumference}
            style={{
              strokeDashoffset,
              strokeLinecap: "round",
              transition: "stroke-dashoffset 1s ease-in-out",
            }}
            className="text-indigo-500"
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-900 leading-none tabular-nums">
            {value}
          </span>
          <span className="text-[10px] font-semibold text-indigo-400 mt-0.5 tabular-nums">
            {percentage}%
          </span>
        </div>
      </div>

      <div className="text-center">
        <div className="inline-flex p-1.5 rounded-lg mb-2 bg-indigo-50 text-indigo-600">
          <Icon size={16} strokeWidth={2.5} />
        </div>
        <h4
          className={`text-xs font-semibold uppercase tracking-wide ${BRAND_THEME.section.title}`}
        >
          {label}
        </h4>
      </div>
    </div>
  );
};

const PunchStatusCards = ({ statistics }) => {
  if (!statistics) return null;

  const total = statistics.totalUsers || 0;

  const stats = [
    {
      label: "Total Users",
      value: statistics.totalUsers || 0,
      icon: UsersIcon,
    },
    {
      label: "Clocked In",
      value: statistics.clockedIn || 0,
      icon: UserCheckIcon,
    },
    {
      label: "Clocked Out",
      value: statistics.clockedOut || 0,
      icon: LogOutIcon,
    },
    {
      label: "On Break",
      value: statistics.onBreak || 0,
      icon: CoffeeIcon,
    },
    {
      label: "Missing",
      value: statistics.notClockedIn || 0,
      icon: UserXIcon,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {stats.map((stat) => (
        <CircularProgress
          key={stat.label}
          value={stat.value}
          total={total}
          label={stat.label}
          icon={stat.icon}
        />
      ))}
    </div>
  );
};

export default PunchStatusCards;
